/**
 * TW1 Identity Platform - API Client
 * Handles all API calls with auto-refresh on 401.
 * Refresh tokens stored in memory (no cookies).
 *
 * NOTE: Emergent's telemetry wraps window.fetch and pre-consumes the body of
 * non-2xx responses, which makes `res.clone().json()` throw and leaves error
 * bodies unreadable from app code. To preserve Dutch detail messages on 4xx/5xx
 * we bypass fetch entirely and use XMLHttpRequest, then expose a Response-like
 * wrapper so every existing caller (res.ok / res.clone().json()) keeps working.
 */

/**
 * Compute the API base URL.
 *
 * `REACT_APP_BACKEND_URL` is honoured when it points to the SAME origin as
 * the page (preview environments, local dev). When it points to a DIFFERENT
 * origin than the page (e.g. production where the frontend is served from
 * id.truckwash1.cloud but the env still has the legacy emergent.host URL),
 * we fall back to relative paths so every API call stays same-origin and
 * the httpOnly `tw1_id_session` cookie + `xhr.withCredentials` flow works
 * without needing CORS `allow_credentials=true`.
 *
 * This is robust against stale env-vars baked into the production bundle
 * via Emergent's secret store (which is "already exists, skipping" on every
 * redeploy and therefore doesn't auto-update with code changes).
 */
function computeBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (!envUrl) return '';
  if (typeof window === 'undefined') return envUrl;
  try {
    const envOrigin = new URL(envUrl).origin;
    return envOrigin === window.location.origin ? envUrl : '';
  } catch {
    return '';
  }
}

const BACKEND_URL = computeBackendUrl();

let accessToken = null;
let refreshToken = null;
let refreshPromise = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setRefreshToken(token) {
  refreshToken = token;
}

export function getRefreshToken() {
  return refreshToken;
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
}

// Execute an HTTP request via XHR (bypasses any fetch-wrapping telemetry) and
// return a Response-like wrapper with the full body buffered as text.
// `withCredentials` is set to true for ALL requests so the same-origin
// `tw1_id_session` cookie is sent on /api/auth/session probes; it has no
// cross-origin effect since we don't enable allow_credentials in CORS.
function xhrRequest(url, { method = 'GET', headers = {}, body = null } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.withCredentials = true;
    Object.entries(headers).forEach(([k, v]) => {
      if (v !== undefined && v !== null) xhr.setRequestHeader(k, v);
    });
    xhr.onload = () => {
      const status = xhr.status;
      const bodyText = xhr.responseText || '';
      const wrapper = {
        ok: status >= 200 && status < 300,
        status,
        statusText: xhr.statusText,
        _bodyText: bodyText,
        async text() { return this._bodyText; },
        async json() {
          try { return JSON.parse(this._bodyText); } catch { return {}; }
        },
        clone() { return this; },
      };
      resolve(wrapper);
    };
    xhr.onerror = () => reject(new TypeError('Netwerkfout'));
    xhr.ontimeout = () => reject(new TypeError('Time-out'));
    xhr.send(body);
  });
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  if (!refreshToken) throw new Error('Sessie verlopen');

  refreshPromise = (async () => {
    try {
      const res = await xhrRequest(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        clearTokens();
        throw new Error('Sessie verlopen');
      }
      const data = await res.json();
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      return data.access_token;
    } catch (err) {
      clearTokens();
      throw err;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function apiFetch(path, options = {}) {
  const url = `${BACKEND_URL}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await xhrRequest(url, {
    method: options.method || 'GET',
    headers,
    body: options.body || null,
  });

  // Auto-refresh on 401
  if (res.status === 401 && refreshToken) {
    try {
      await refreshAccessToken();
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await xhrRequest(url, {
        method: options.method || 'GET',
        headers,
        body: options.body || null,
      });
    } catch {
      // Refresh failed, return the original 401
    }
  }

  return res;
}

// Auth API
export const authAPI = {
  login: (email, password) =>
    xhrRequest(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }),

  logout: () => apiFetch('/api/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  }),

  refresh: () => {
    if (!refreshToken) return Promise.resolve({ ok: false, status: 401, async json() { return {}; }, async text() { return ''; }, clone() { return this; } });
    return xhrRequest(`${BACKEND_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  },

  me: () => apiFetch('/api/auth/me'),

  // Same-origin SSO probe — sends the httpOnly `tw1_id_session` cookie via
  // xhr.withCredentials. Returns 200 + tokens when an existing session exists,
  // or 401 when the user must log in fresh. Used by /login on mount.
  session: () =>
    xhrRequest(`${BACKEND_URL}/api/auth/session`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }),

  forgotPassword: (email) =>
    xhrRequest(`${BACKEND_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token, new_password) =>
    xhrRequest(`${BACKEND_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password }),
    }),

  changePassword: (current_password, new_password) =>
    apiFetch('/api/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ current_password, new_password }),
    }),
};

// Users API
export const usersAPI = {
  list: () => apiFetch('/api/users'),
  get: (id) => apiFetch(`/api/users/${id}`),
  create: (data) => apiFetch('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deactivate: (id) => apiFetch(`/api/users/${id}/deactivate`, { method: 'PUT' }),
  activate: (id) => apiFetch(`/api/users/${id}/activate`, { method: 'PUT' }),
  resetPassword: (id) => apiFetch(`/api/users/${id}/reset-pw`, { method: 'POST' }),
  delete: (id) => apiFetch(`/api/users/${id}`, { method: 'DELETE' }),
};

// Tools API
export const toolsAPI = {
  list: () => apiFetch('/api/tools'),
  create: (data) => apiFetch('/api/tools', { method: 'POST', body: JSON.stringify(data) }),
  update: (toolId, data) => apiFetch(`/api/tools/${toolId}`, { method: 'PUT', body: JSON.stringify(data) }),
  regenerateKey: (toolId) => apiFetch(`/api/tools/${toolId}/regenerate-key`, { method: 'POST' }),
  delete: (toolId) => apiFetch(`/api/tools/${toolId}`, { method: 'DELETE' }),
  getRollen: (toolId) => apiFetch(`/api/tools/${toolId}/rollen`),
  updateRollen: (toolId, beschikbare_rollen) =>
    apiFetch(`/api/tools/${toolId}/rollen`, {
      method: 'PUT',
      body: JSON.stringify({ beschikbare_rollen }),
    }),
};

// Rechten API
export const rechtenAPI = {
  getForUser: (userId) => apiFetch(`/api/rechten/${userId}`),
  getForUserTool: (userId, toolId) => apiFetch(`/api/rechten/${userId}/${toolId}`),
  update: (userId, toolId, data) =>
    apiFetch(`/api/rechten/${userId}/${toolId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getForTool: (toolId) => apiFetch(`/api/rechten/tool/${toolId}`),
  getMine: (toolId) => apiFetch(`/api/user-rechten/${toolId}`),
};

// Sessions API
export const sessionsAPI = {
  list: () => apiFetch('/api/sessions'),
  terminate: (sessionId) => apiFetch(`/api/sessions/${sessionId}`, { method: 'DELETE' }),
};

// Activity API
export const activityAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams();
    if (params.user_id) query.set('user_id', params.user_id);
    if (params.tool_id) query.set('tool_id', params.tool_id);
    if (params.from) query.set('from_date', params.from);
    if (params.to) query.set('to_date', params.to);
    if (params.limit) query.set('limit', params.limit);
    if (params.skip) query.set('skip', params.skip);
    const qs = query.toString();
    return apiFetch(`/api/activity${qs ? '?' + qs : ''}`);
  },
};

// Validate API
export const validateAPI = {
  validate: (token) =>
    xhrRequest(`${BACKEND_URL}/api/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  validateRedirect: (redirectUrl) =>
    apiFetch('/api/sso/validate-redirect', {
      method: 'POST',
      body: JSON.stringify({ redirect_url: redirectUrl }),
    }),
};

// Locations API
export const locationsAPI = {
  list: () => apiFetch('/api/locations'),
  create: (data) => apiFetch('/api/locations', { method: 'POST', body: JSON.stringify(data) }),
  update: (slug, data) => apiFetch(`/api/locations/${slug}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (slug) => apiFetch(`/api/locations/${slug}`, { method: 'DELETE' }),
};
